import { useState } from 'react';
import {
  LineChart, BarChart, AreaChart, PieChart,
  Line, Bar, Area, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const COLORS = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316'];

const CHART_TYPES = ['line', 'bar', 'area', 'pie'];

function buildRechartsData(labels, datasets) {
  return labels.map((label, i) => {
    const point = { name: label };
    datasets.forEach(ds => { point[ds.label] = ds.data[i]; });
    return point;
  });
}

function LineChartView({ data, datasets }) {
  return (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
        <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="var(--sidebar-muted)" />
        <YAxis tick={{ fontSize: 11 }} stroke="var(--sidebar-muted)" width={48} />
        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-surface)' }} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {datasets.map((ds, i) => (
          <Line key={ds.label} type="monotone" dataKey={ds.label} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

function BarChartView({ data, datasets }) {
  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
        <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="var(--sidebar-muted)" />
        <YAxis tick={{ fontSize: 11 }} stroke="var(--sidebar-muted)" width={48} />
        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-surface)' }} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {datasets.map((ds, i) => (
          <Bar key={ds.label} dataKey={ds.label} fill={COLORS[i % COLORS.length]} radius={[4, 4, 0, 0]} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

function AreaChartView({ data, datasets }) {
  return (
    <ResponsiveContainer width="100%" height={260}>
      <AreaChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
        <defs>
          {datasets.map((ds, i) => (
            <linearGradient key={ds.label} id={`grad-${i}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={COLORS[i % COLORS.length]} stopOpacity={0.2} />
              <stop offset="95%" stopColor={COLORS[i % COLORS.length]} stopOpacity={0} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
        <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="var(--sidebar-muted)" />
        <YAxis tick={{ fontSize: 11 }} stroke="var(--sidebar-muted)" width={48} />
        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-surface)' }} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {datasets.map((ds, i) => (
          <Area key={ds.label} type="monotone" dataKey={ds.label} stroke={COLORS[i % COLORS.length]} strokeWidth={2} fill={`url(#grad-${i})`} />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}

function PieChartView({ labels, datasets }) {
  const pieData = labels.map((label, i) => ({ name: label, value: datasets[0]?.data[i] ?? 0 }));
  return (
    <ResponsiveContainer width="100%" height={260}>
      <PieChart>
        <Pie data={pieData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
          {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
        </Pie>
        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-surface)' }} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

export default function ChartBlock({ raw }) {
  const [activeType, setActiveType] = useState(null); // null = use chart's own type
  const [error, setError] = useState(null);

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return (
      <div className="chart-parse-error">
        ⚠️ Could not render chart — invalid JSON.
        <pre className="chart-raw">{raw}</pre>
      </div>
    );
  }

  const { title, labels = [], datasets = [] } = parsed;
  const chartType = activeType || parsed.type || 'line';

  if (!labels.length || !datasets.length) {
    return <div className="chart-parse-error">⚠️ Chart data is empty.</div>;
  }

  const data = buildRechartsData(labels, datasets);

  const renderChart = () => {
    switch (chartType) {
      case 'bar':   return <BarChartView data={data} datasets={datasets} />;
      case 'area':  return <AreaChartView data={data} datasets={datasets} />;
      case 'pie':   return <PieChartView labels={labels} datasets={datasets} />;
      default:      return <LineChartView data={data} datasets={datasets} />;
    }
  };

  return (
    <div className="chart-block">
      <div className="chart-header">
        {title && <span className="chart-title">{title}</span>}
        <div className="chart-type-switcher">
          {CHART_TYPES.map(type => (
            <button
              key={type}
              className={`chart-type-btn ${chartType === type ? 'chart-type-active' : ''}`}
              onClick={() => setActiveType(type)}
              title={`Switch to ${type} chart`}
            >
              {type}
            </button>
          ))}
        </div>
      </div>
      <div className="chart-body">
        {renderChart()}
      </div>
    </div>
  );
}
